import { Sensor, useAirQualityData } from 'common';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { YStack, XStack, Text } from 'tamagui';
import { tokens } from 'tamagui-config/src/tokens';

import { LoadingOverlay } from '../../../components/common';
import { HubLineChart } from '../../../components/common/HubLineChart';

interface InHomeAirQualityContentProps {
  sensor?: Sensor;
}

export const InHomeAirQualityContent: React.FC<InHomeAirQualityContentProps> = ({ sensor }) => {
  const { t } = useTranslation();
  const { data, loading, error } = useAirQualityData({
    sensorId: sensor?.id,
    days: 6,
    enabled: !!sensor?.id,
  });
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const dailyIAQ = data.reduce(
      (acc, day) => {
        // Parse the date in the local timezone by appending the time (T00:00:00)
        // This ensures the date is interpreted in the user's local timezone
        const date = new Date(`${day.label}T00:00:00`);
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });

        if (!acc[dayOfWeek]) {
          acc[dayOfWeek] = { sum: 0, count: 0, timestamp: date.getTime() };
        }

        if (date.getTime() > acc[dayOfWeek].timestamp) {
          acc[dayOfWeek].timestamp = date.getTime();
        }

        acc[dayOfWeek].sum += day.iaq;
        acc[dayOfWeek].count += 1;
        if (day.iaq > 1) {
          console.log(`IAQ is greater than 1: ${JSON.stringify(day)}`);
        }
        return acc;
      },
      {} as Record<string, { sum: number; count: number; timestamp: number }>,
    );

    // Show last 7 days ending with today
    const chartData: Array<{ value: number | undefined; label: string; timestamp: number | undefined }> = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });

      const dayData = dailyIAQ[dayLabel];

      if (dayData) {
        chartData.push({
          value: Math.round(dayData.sum / dayData.count),
          label: dayLabel,
          timestamp: dayData.timestamp,
        });
      } else {
        chartData.push({
          value: undefined, // Using undefined instead of null for missing data points
          label: dayLabel,
          timestamp: date.getTime(),
        });
        console.log(`No data for ${dayLabel} (${date.toLocaleDateString()})`);
      }
    }
    console.log(`chartData: ${JSON.stringify(chartData)}`);
    return chartData;
  }, [data]);

  const todayIAQ = React.useMemo(() => {
    if (!data || data.length === 0) return 0;

    const mostRecentData = [...data].sort(
      (a, b) => new Date(b.label).getTime() - new Date(a.label).getTime(),
    )[0];

    return Math.round(mostRecentData.iaq);
  }, [data]);

  if (loading) {
    return <LoadingOverlay isVisible={true} />;
  }

  if (error || !chartData.length) {
    return (
      <XStack flex={1} justifyContent="center" alignItems="center">
        <Text fontSize="$6" color={tokens.color.textLight} textAlign="center">
          {error || t('air.noData')}
        </Text>
      </XStack>
    );
  }

  return (
    <YStack gap={'$4'}>
      <XStack gap={'$4'} alignItems="baseline">
        <Text fontFamily="$heading" fontWeight="$9.5" fontSize="$9.5" color={tokens.color.white12}>
          {`${todayIAQ} IAQ`}
        </Text>
        <Text fontSize="$5.5" color={tokens.color.white12}>
          {todayIAQ < 50
            ? t('air.status.good')
            : todayIAQ < 100
              ? t('air.status.moderate')
              : t('air.status.bad')}
        </Text>
      </XStack>
      <HubLineChart data={chartData} unit={''} minValue={0}/>
    </YStack>
  );
};
